import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * Free gift.
 *
 * The theme only offers and adds the gift and keeps the cart consistent with the rules. The price
 * is made $0 at checkout by an automatic "Buy X get Y" discount in Shopify admin, so the gift is
 * only ever free while the cart really qualifies.
 *
 * The rules are evaluated in Liquid (snippets/free-gift-state.liquid) because they depend on
 * product tags, which /cart.js doesn't expose. After every cart change the state is re-fetched
 * through sections/free-gift-state.liquid and the cart is corrected when needed:
 * - the gift is removed once no qualifying product is left
 * - the gift is kept at a quantity of 1, in a single line
 *
 * @typedef {object} FreeGiftState
 * @property {boolean} enabled
 * @property {boolean} available
 * @property {number} giftProductId
 * @property {number} giftVariantId
 * @property {number} minQuantity
 * @property {number} qualifyingQuantity
 * @property {boolean} qualifies
 * @property {number} giftQuantity
 * @property {Array<{key: string, quantity: number}>} giftLines
 */

const STATE_SECTION_ID = 'free-gift-state';
const STATE_SELECTOR = '[data-free-gift-state]';

// Guards against an endless correct → re-check loop if the cart refuses a change.
const MAX_ENFORCE_PASSES = 3;

/** @type {FreeGiftState | null} */
let currentState = null;

/** @type {Promise<void> | null} */
let refreshing = null;
let refreshRequested = false;

/**
 * @param {ParentNode} root
 * @returns {FreeGiftState | null}
 */
function readState(root) {
  const element = root.querySelector(STATE_SELECTOR);
  if (!element?.textContent) return null;

  try {
    return JSON.parse(element.textContent);
  } catch (error) {
    console.warn('[free-gift] Invalid state JSON', error);
    return null;
  }
}

/** @returns {Promise<FreeGiftState | null>} */
async function fetchState() {
  const root = window.Shopify?.routes?.root || '/';
  const response = await fetch(`${root}?section_id=${STATE_SECTION_ID}`, {
    credentials: 'same-origin',
    cache: 'no-store',
  });
  if (!response.ok) throw new Error(`Failed to fetch free gift state: ${response.status}`);

  return readState(new DOMParser().parseFromString(await response.text(), 'text/html'));
}

/**
 * Quantities each gift line should have: the first line keeps 1 while the cart qualifies,
 * everything else goes to 0.
 * @param {FreeGiftState} state
 * @returns {Record<string, number>}
 */
function getCorrections(state) {
  /** @type {Record<string, number>} */
  const updates = {};

  state.giftLines.forEach((line, index) => {
    const wanted = state.qualifies && index === 0 ? 1 : 0;
    if (line.quantity !== wanted) updates[line.key] = wanted;
  });

  return updates;
}

/**
 * Applies the corrections in one /cart/update request and announces it like any other cart
 * change, so the cart drawer, cart page and cart bubble re-render.
 * @param {Record<string, number>} updates
 */
async function applyCorrections(updates) {
  const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
    .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
    .filter(Boolean);

  const lines = Object.entries(updates).map(([id, quantity]) => ({ id, quantity }));
  const deferred = CartLinesUpdateEvent.createPromise();

  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: lines.every((line) => line.quantity === 0) ? 'remove' : 'update',
      context: 'cart',
      lines,
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(
      Theme.routes.cart_update_url,
      fetchConfig('json', {
        body: JSON.stringify({
          updates,
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      })
    );
    const cart = await response.json();
    if (cart.status) throw new Error(cart.description || cart.message || 'Free gift update failed');

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        sections: cart.sections,
        items: cart.items,
        itemCount: cart.item_count,
        source: 'free-gift',
        didError: false,
      },
    });
    return true;
  } catch (error) {
    deferred.reject(error);
    console.warn('[free-gift] Could not correct the cart', error);
    return false;
  }
}

/**
 * Re-evaluates the rules against the live cart and corrects it until it's consistent.
 * Calls made while a refresh is running are folded into one extra pass.
 */
function refresh() {
  if (refreshing) {
    refreshRequested = true;
    return refreshing;
  }

  refreshing = (async () => {
    for (let pass = 0; pass < MAX_ENFORCE_PASSES; pass++) {
      refreshRequested = false;

      const state = await fetchState();
      currentState = state;

      const updates = state ? getCorrections(state) : {};
      const corrected = Object.keys(updates).length > 0 && (await applyCorrections(updates));

      if (!corrected && !refreshRequested) break;
    }
  })()
    .catch((error) => console.warn('[free-gift] Refresh failed', error))
    .finally(() => {
      refreshing = null;
    });

  return refreshing;
}

/** @param {Event} event */
function onCartLinesUpdate(event) {
  /** @type {CartLinesUpdateEvent} */ (event).promise
    ?.then(() => refresh())
    .catch(() => {
      // A failed cart change leaves the cart as it was; nothing to correct.
    });
}

function init() {
  const initialState = readState(document);
  currentState = initialState;

  document.addEventListener(StandardEvents.cartLinesUpdate, onCartLinesUpdate);

  // Carts can change outside this page (another tab, a full-page cart form post), so correct on load too.
  if (initialState && Object.keys(getCorrections(initialState)).length > 0) refresh();
}

/**
 * The "free gift unlocked" pop-up. Rendered inside product-form-component by buy-buttons.liquid,
 * only for products that qualify. ProductFormComponent asks it before adding to cart.
 */
class FreeGiftOffer extends HTMLElement {
  /** @type {boolean} */
  #declined = false;

  /** @type {((accepted: boolean) => void) | null} */
  #resolve = null;

  connectedCallback() {
    this.dialog?.addEventListener('click', this.#onClick);
    this.dialog?.addEventListener('close', this.#onClose);
  }

  disconnectedCallback() {
    this.dialog?.removeEventListener('click', this.#onClick);
    this.dialog?.removeEventListener('close', this.#onClose);
  }

  /** @returns {HTMLDialogElement | null} */
  get dialog() {
    return this.querySelector('dialog');
  }

  /** @returns {string} */
  get giftVariantId() {
    return String(currentState?.giftVariantId ?? '');
  }

  /**
   * Whether adding `quantity` units of this product unlocks a gift the cart doesn't have yet.
   * @param {number} quantity
   * @returns {boolean}
   */
  shouldOffer(quantity) {
    const state = currentState;
    if (!state || this.#declined) return false;

    return (
      state.enabled &&
      state.available &&
      state.giftQuantity === 0 &&
      state.qualifyingQuantity + quantity >= state.minQuantity
    );
  }

  /**
   * Opens the pop-up and resolves with the customer's answer. Declining is remembered for the
   * rest of this page view so further adds aren't interrupted again.
   * @returns {Promise<boolean>}
   */
  ask() {
    const { dialog } = this;
    if (!dialog) return Promise.resolve(false);

    return new Promise((resolve) => {
      this.#resolve = resolve;
      dialog.showModal();
      dialog.querySelector('[data-free-gift-accept]')?.focus();
    });
  }

  /** @param {boolean} accepted */
  #answer(accepted) {
    const resolve = this.#resolve;
    this.#resolve = null;
    if (!resolve) return;

    if (!accepted) this.#declined = true;
    if (this.dialog?.open) this.dialog.close();
    resolve(accepted);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    if (target.closest('[data-free-gift-accept]')) {
      this.#answer(true);
    } else if (target.closest('[data-free-gift-decline]') || target === this.dialog) {
      // The dialog itself is only the click target on its backdrop.
      this.#answer(false);
    }
  };

  // Esc key, or the dialog closed any other way.
  #onClose = () => this.#answer(false);
}

if (!customElements.get('free-gift-offer')) {
  customElements.define('free-gift-offer', FreeGiftOffer);
}

init();
